import { format } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  ALERT_LEAD_MINUTES,
  ALERT_TRAIL_MINUTES,
  TEAM_LABELS,
  activeEvent,
  nextEvent,
  type AlertEvent,
} from '@/lib/api/alertEvents';
import { setMonitoringEnabled } from '@/lib/api/monitoring';
import type { SmsLogEntry } from '@/lib/api/smsLog';

const hours = (minutes: number) =>
  minutes % 60 === 0 ? `${minutes / 60} h` : `${(minutes / 60).toFixed(1)} h`;

type Props = {
  enabled: boolean;
  events: AlertEvent[] | undefined;
  now: number;
  smsLog: SmsLogEntry[] | undefined;
  label: (number: string) => string;
};

export function AlertingTab({ enabled, events, now, smsLog, label }: Props) {
  const active = activeEvent(events, now);
  const upcoming = nextEvent(events, now);

  return (
    <div className="w-full">
      {/* Global on/off */}
      <div className="flex items-center justify-between rounded-md border p-4">
        <div>
          <p className="font-medium">Alerting On Schedule</p>
          <p className="text-sm text-muted-foreground">
            When on, monitored boxes that fail a check trigger an SMS each cycle
            — but only inside a scheduled event window (kickoff −
            {` ${hours(ALERT_LEAD_MINUTES)}`} to kickoff +
            {` ${hours(ALERT_TRAIL_MINUTES)}`}). Outside every window, and when
            off, no SMS is sent.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-muted-foreground">
            {enabled ? 'On' : 'Off'}
          </span>
          <Switch
            checked={enabled}
            onCheckedChange={(v) => setMonitoringEnabled(v)}
          />
        </div>
      </div>

      {/* Live window status */}
      <div className="mt-4 rounded-md border p-4">
        <div className="flex flex-wrap items-center gap-3">
          <span className="font-medium">Right now:</span>
          {!enabled ? (
            <Badge variant="outline">Alerting off — no SMS will be sent</Badge>
          ) : active ? (
            <Badge variant="destructive">
              Window open until {format(active.windowEnd, 'HH:mm')}
            </Badge>
          ) : (
            <Badge variant="secondary">Outside every window — SMS paused</Badge>
          )}
        </div>
        <p className="mt-2 text-sm text-muted-foreground">
          {active ? (
            <>
              {TEAM_LABELS[active.team]} vs {active.opponent} —{' '}
              {active.timeTbd
                ? 'kickoff TBA, covering the whole game day'
                : `kickoff ${format(active.kickoff, 'MMM d, HH:mm')}`}
              .
            </>
          ) : upcoming ? (
            <>
              Next window opens{' '}
              {format(upcoming.windowStart, 'EEE MMM d, HH:mm')} for{' '}
              {TEAM_LABELS[upcoming.team]} vs {upcoming.opponent}
              {upcoming.timeTbd
                ? ' (kickoff TBA)'
                : ` (kickoff ${format(upcoming.kickoff, 'HH:mm')})`}
              .
            </>
          ) : (
            'No upcoming events scheduled — add games on the Event Schedule tab.'
          )}
        </p>
      </div>

      {/* Sent SMS log */}
      <h2 className="mt-8 text-xl font-semibold">Sent SMS log</h2>
      <div className="mt-4 rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Time</TableHead>
              <TableHead>Recipients</TableHead>
              <TableHead>Message</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(smsLog ?? []).length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} className="h-24 text-center">
                  No SMS sent yet.
                </TableCell>
              </TableRow>
            ) : (
              (smsLog ?? []).map((entry) => (
                <TableRow key={entry.id}>
                  <TableCell className="whitespace-nowrap text-muted-foreground">
                    {entry.sentAt ? format(entry.sentAt, 'MMM d, HH:mm') : '—'}
                  </TableCell>
                  <TableCell>
                    {entry.recipients.map((n) => label(n)).join(', ')}
                    {entry.failed && entry.failed.length > 0 && (
                      <span
                        className="ml-1 text-destructive"
                        title={entry.failed
                          .map((f) => `${f.to}: ${f.error}`)
                          .join('\n')}
                      >
                        · {entry.failed.length} failed
                      </span>
                    )}
                  </TableCell>
                  {/* Messages are one box per line — keep the breaks. */}
                  <TableCell className="whitespace-pre-line">
                    {entry.message}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
